import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import * as _ from 'lodash';
import { catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';
import Session from 'supertokens-web-js/recipe/session';
import {
  CartItemsDataV2,
  CartItemsV2,
  CartSummaryV2,
  CommonCartServiceV2,
  CourseV2,
  CredentialsService,
  LoaderService,
  LocalStorage,
  PartialEntity,
} from 'thkee-common';
import { cartKeyV2, CartStateV2, initialCartStateV2 } from './cart-state.model';
import { CartActionsV2 } from './cart.actions';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CartServiceV2 {
  private _cart: CartStateV2 = initialCartStateV2;
  cartId!: string;
  courseIds: string[] = [];
  // mainCartKey = this.authService.sessionExist() ? cartKeyV2 : cartKeyV2 + '_guest';
  mainCartKey!: string;
  purchasedItem: string | undefined;

  constructor(
    private comCartService: CommonCartServiceV2,
    private credentialsService: CredentialsService,
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loaderService: LoaderService,
    private authService: AuthenticationService
  ) {
    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
      this.purchasedItem = params['item'];
    });
  }

  getCart() {
    return of(this._cart);
  }

  get cart(): CartStateV2 {
    return this._cart;
  }

  hasCart(): boolean {
    return !!this._cart;
  }

  cleanUserCart() {
    this.setCart(initialCartStateV2);
    return of(initialCartStateV2);
  }

  initUserCart() {
    return from(Session.doesSessionExist()).pipe(
      switchMap((exists) => {
        if (exists) {
          this.mainCartKey = cartKeyV2;
        } else {
          this.mainCartKey = cartKeyV2 + '_guest';
        }
        const savedCart = localStorage.getItem(this.mainCartKey);
        if (savedCart) {
          this._cart = JSON.parse(savedCart);
        }

        const newData = {
          ...initialCartStateV2,
          ...this._cart,
        };
        this.setCart(newData);
        return of(newData);
      })
    );
  }

  private async init(data: CartStateV2) {
    const isLogged = await this.authService.isLogged();
    this.mainCartKey = isLogged ? cartKeyV2 : cartKeyV2 + '_guest';

    const savedCart = localStorage.getItem(this.mainCartKey);
    if (savedCart) {
      this._cart = JSON.parse(savedCart);
    }

    this.setCart({ ...data, ...this._cart });
  }

  loadCart(): Observable<CartStateV2> {
    this.loaderService.loaderAction('shopping-cart-loader', 'loading');
    return from(this.authService.isLogged()).pipe(
      switchMap((loggedin) => {
        if (loggedin) {
          return this.comCartService.getCartItems().pipe(
            map((data: CartItemsV2) => {
              this.loaderService.loaderAction('shopping-cart-loader', 'success');
              let cartData = this._cart ? this._cart : initialCartStateV2;
              const items = data.items;
              const courseIds = _.map(items, 'id');
              const courseCodes = _.map(items, 'code');

              const courseData = items.map((course: any) => {
                let author = '';
                if (course.user) {
                  author = typeof course.user === 'number' ? 'N/A' : `${course.user.fullname}`;
                }
                return {
                  id: course.id,
                  course_id: course.course_id,
                  code: course.code,
                  title: course.title,
                  subtitle: course.subtitle,
                  average_rating: course.average_rating ?? 0,
                  slug: course.slug,
                  favorite: course.favorite,
                  image_url: course.image_url,
                  is_promote: course.is_promote,
                  language: course.language,
                  total_rate: Math.round(course.total_rate) || 0,
                  total_reviews: course.total_reviews || 0,
                  author: author,
                };
              });

              const newData = {
                ...cartData,
                courses: courseIds,
                coursesData: courseData,
              };
              this.setCart(newData);
              // this.mergedGuestCart();
              if (courseCodes.length > 0) {
                this.cartSummary(courseCodes).subscribe();
              }
              return newData;
            })
          );
        } else {
          const guestToken = this.getGuestToken();
          return this.comCartService.getGuestCartItems(guestToken).pipe(
            map((data: CartItemsV2) => {
              this.loaderService.loaderAction('shopping-cart-loader', 'success');
              let cartData = this._cart ? this._cart : initialCartStateV2;
              const items = data.items;
              const courseIds = _.map(items, 'id');
              const courseCodes = _.map(items, 'code');

              const courseData = items.map((course: any) => {
                let author = '';
                if (course.user) {
                  author = typeof course.user === 'number' ? 'N/A' : `${course.user.fullname}`;
                }
                return {
                  id: course.id,
                  course_id: course.course_id,
                  code: course.code,
                  title: course.title,
                  subtitle: course.subtitle,
                  average_rating: course.average_rating ?? 0,
                  slug: course.slug,
                  favorite: course.favorite,
                  image_url: course.image_url,
                  is_promote: course.is_promote,
                  language: course.language,
                  total_rate: Math.round(course.total_rate) || 0,
                  total_reviews: course.total_reviews || 0,
                  author: author,
                };
              });

              const newData = {
                ...cartData,
                courses: courseIds,
                coursesData: courseData,
              };
              this.setCart(newData);
              if (courseCodes.length > 0) {
                this.cartSummary(courseCodes).subscribe();
              }
              return newData;
            })
          );
        }
      })
    );
  }

  addToCart(courseData: PartialEntity<CourseV2>): Observable<CartStateV2> {
    this.loaderService.loaderAction('shopping-cart-summary-loader', 'loading');
    const item = {
      id: courseData.id,
      course_id: courseData.course_id,
      code: courseData.code,
      title: courseData.title,
      subtitle: courseData.subtitle,
      average_rating: courseData.average_rating,
      slug: courseData.slug,
      favorite: courseData.favorite,
      image_url: courseData.image_url,
      is_promote: courseData.is_promote,
      language: courseData.language,
      total_rate: courseData.total_rate || 0,
      total_reviews: courseData.total_reviews || 0,
      author: courseData.user.fullname,
    };

    if (this._cart) {
      this._cart = {
        ...this._cart,
        courses: [...this._cart.courses, item.id],
        coursesData: [...this.cart.coursesData, item as CartItemsDataV2],
      };
      this.setCart(this._cart);
    }
    const payload = {
      product: item.id,
    };
    const courseCodes = _.map(this.cart.coursesData, 'code');

    this.authService.isLogged().then((isLogged) => {
      if (isLogged) {
        this.comCartService
          .addToCart(payload)
          .pipe(
            tap(() => {
              this.cartSummary(courseCodes).subscribe();
            }),
            catchError((error) => {
              return of(null);
            })
          )
          .subscribe();
        return of(this._cart);
      } else {
        const guestToken = this.getGuestToken();
        this.comCartService
          .addToGuestCart(payload, guestToken)
          .pipe(
            tap(() => {
              this.cartSummary(courseCodes).subscribe();
            }),
            catchError((error) => {
              return of(null);
            })
          )
          .subscribe();
        return of(this._cart);
      }
    });

    return of(this._cart);
  }

  removeToCart(id: string) {
    if (this._cart) {
      this._cart = {
        ...this._cart,
        courses: _.without(this._cart.courses, id),
        coursesData: _.filter(this._cart.coursesData, (o) => {
          return id != o.id;
        }),
      };
      this.setCart(this._cart);
    }
    const payload = {
      product: id,
    };
    const courseCodes = _.map(this._cart.coursesData, 'code');
    this.cartSummary(courseCodes).subscribe();

    this.authService.isLogged().then((loggedIn) => {
      if (loggedIn) {
        this.comCartService.removeToCart(payload).subscribe();
      } else {
        const guestToken = this.getGuestToken();
        this.comCartService.removeToGuestCart(payload, guestToken).subscribe();
      }
    });
    return of(this._cart);
  }

  // Guest token managed
  getGuestToken() {
    return JSON.stringify(LocalStorage.getObject<string>('guest_cart_token'));
  }

  removeGuestToken() {
    if (this.getGuestToken()) {
      LocalStorage.removeStorageItem('guest_cart_token');
    }
  }

  mergedGuestCart() {
    const guestToken = this.getGuestToken();
    if (guestToken) {
      this.comCartService.mergedGuestCart(guestToken).subscribe({
        next: (res) => {
          this.removeGuestToken();
          if (LocalStorage.getObject<string>('cart_guest')) {
            LocalStorage.removeStorageItem('cart_guest');
          }
          if (this.router.url === '/cart') {
            window.location.replace('/cart');
          }
        },
      });
    }
  }

  private cartSummary(courseCodes: Array<string | undefined>): Observable<CartSummaryV2> {
    this.loaderService.loaderAction('shopping-cart-summary-loader', 'loading');
    const courseCode = this.purchasedItem ? [this.purchasedItem] : courseCodes;
    const summaryPayload = {
      payables: courseCode,
    };
    return from(
      this.authService.sessionExist().pipe(
        switchMap((loggedIn) => {
          if (loggedIn) {
            return this.comCartService.getCartSummary(summaryPayload).pipe(
              map((data: CartSummaryV2) => {
                this.loaderService.loaderAction('shopping-cart-summary-loader', 'success');
                this.store.dispatch(CartActionsV2.updateCartSummary({ summary: data }));
                return data;
              })
            );
          } else {
            const guestToken = this.getGuestToken();
            return this.comCartService.getGuestCartSummary(summaryPayload, guestToken).pipe(
              map((data: CartSummaryV2) => {
                this.loaderService.loaderAction('shopping-cart-summary-loader', 'success');
                this.store.dispatch(CartActionsV2.updateCartSummary({ summary: data }));
                return data;
              })
            );
          }
        })
      )
    );
  }

  setCart(cartData?: CartStateV2 | null, key?: string) {
    this._cart = cartData ? cartData : initialCartStateV2;
    let currentKey = key ? key : this.mainCartKey;
    if (cartData) {
      localStorage.setItem(
        currentKey,
        JSON.stringify({ cartId: cartData.cartId, courses: cartData.courses, coursesData: cartData.coursesData })
      );
    } else {
      localStorage.removeItem(currentKey);
    }
  }

  // TODO: Will implement latter
  applyCredit(credit: number) {
    return of(this._cart);
  }

  applyCoupon(code: string) {
    return of(this._cart);
  }
}
